// Real HR data client for the employee/leave services.
// Auth is the httpOnly session cookie (sent via withCredentials) + CSRF token —
// see lib/session.ts. (Cookie + CSRF, same model as lib/apiClient.ts.)
import axios, { AxiosInstance } from 'axios';
import { recordRequestOutcome, recordRequestSuccess } from '@/features/shared/components/recovery/connectionSignal';
import { refreshAccessToken } from './authRefresh';
import { attachCookieAuth } from '@/lib/session';

const EMPLOYEE_URL = import.meta.env.VITE_EMPLOYEE_URL || 'http://localhost:4002';
const LEAVE_URL = import.meta.env.VITE_LEAVE_URL || 'http://localhost:8002';

/** True once we've detected the session token is missing/expired this page-load. */
export function isSessionExpired(): boolean {
  return sessionStorage.getItem('hr_session_expired') === '1';
}

// When the access token is dead AND a silent refresh fails, the session is
// unrecoverable. Rather than leave the user staring at errors (which reads as
// "the app is broken"), sign them out cleanly and send them to /login with a
// friendly notice. Fires at most once per page-load and never loops on an auth
// page. The workspace is preserved in the URL so login only needs a password.
let loggingOut = false;
function forceLogout() {
  if (loggingOut) return;
  loggingOut = true;

  const ws =
    localStorage.getItem('currentWorkspace') ||
    localStorage.getItem('currentTenantSlug') ||
    localStorage.getItem('tenantSlug') ||
    '';

  // Mirror useLogout's local cleanup (we're outside React here, so no hook).
  // Keep the workspace hints so the login form can pre-fill.
  [
    'accessToken', 'refreshToken', 'persist:root', 'user', 'userId', 'userEmail',
    'userName', 'userRole', 'auth_token', 'access_token', 'refresh_token',
  ].forEach((k) => { try { localStorage.removeItem(k); } catch { /* ignore */ } });
  try { sessionStorage.removeItem('hr_profile'); } catch { /* ignore */ }

  const onAuthPage = /\/(login|signin|signup|forgot-password|reset-password|onboarding)/.test(
    window.location.pathname,
  );
  if (!onAuthPage) {
    // Don't carry a (possibly stale) workspace into the login URL — it pre-filled
    // the wrong company. Land on a clean /login?expired=1.
    window.location.assign('/login?expired=1');
  }
}

/**
 * Is this failure one a silent refresh can fix?
 *
 * Exported so the rule is testable: the cost of getting it wrong is invisible
 * either way. Too narrow and a recoverable request dies with a confusing
 * message; too broad and a genuine permission refusal is retried, hidden, and
 * reported as something it is not.
 *
 * A STALE CSRF TOKEN BELONGS HERE. It is bound to the session, so when the
 * session rotates or ages out the copy held in memory stops matching, and the
 * gateway answers 403 {message: 'Invalid or missing CSRF token'} — `message`,
 * the gateway's shape, not FastAPI's `detail`. That was not recognised, so it
 * was never refreshed and never reported as a dead session: the user got a
 * toast about a CSRF token for an action that only needed a new one, and the
 * action silently did not happen. Refreshing re-captures the token
 * (authRefresh calls setCsrf), so it recovers exactly like a dead access
 * token.
 *
 * Found by driving the attendance screens: "Forget this mapping" failed this
 * way once the session had aged, while POSTs made earlier in the same session
 * had gone through.
 */
export function isRecoverableAuthFailure(status: number | undefined, data: unknown): boolean {
  if (status === 401) return true;
  if (status !== 403) return false;
  const body = (data ?? {}) as { detail?: unknown; message?: unknown };
  if (body.detail === 'Not authenticated') return true;
  // Only a CSRF complaint — never a plain "Forbidden" or an origin refusal,
  // which are answers in their own right and must reach the user as they are.
  return /csrf/i.test(String(body.message ?? body.detail ?? ''));
}

export function withAuth(baseURL: string): AxiosInstance {
  const instance = axios.create({ baseURL, timeout: 20000, withCredentials: true });
  instance.interceptors.request.use(attachCookieAuth);
  // On an auth failure, silently refresh the access token (httpOnly refresh
  // cookie) once and retry the original request. Only if refresh fails do we
  // surface a dead session.
  instance.interceptors.response.use(
    (r) => {
      // One request getting through is proof the connection works.
      recordRequestSuccess();
      return r;
    },
    async (err) => {
      // ⚠️ Feeds the in-app connection banner. Only a failure with NO response
      // counts — a 4xx/5xx means we REACHED the server, which is a different
      // problem and must not put "cannot reach KaziPeople" on screen.
      recordRequestOutcome(err?.response?.status);
      // 401 = token rejected; 403 with "Not authenticated" = header missing
      // (FastAPI HTTPBearer). Both mean the access token is dead.
      const status = err?.response?.status;
      const authFail = isRecoverableAuthFailure(status, err?.response?.data);
      const original = err?.config;

      if (authFail && original && !original._retried) {
        original._retried = true;
        const refreshed = await refreshAccessToken();
        if (refreshed) {
          // refreshAccessToken now rotates the httpOnly cookie; a truthy result
          // means "retry" — the cookie carries the new credential, no Bearer.
          return instance(original);
        }
        sessionStorage.setItem('hr_session_expired', '1');
        err.isSessionExpired = true;
        // Session is unrecoverable — sign out + redirect instead of leaving the
        // user on a broken page thinking the app is down.
        forceLogout();
      }
      return Promise.reject(err);
    },
  );
  return instance;
}

const employeeApi = withAuth(EMPLOYEE_URL);
const leaveApi = withAuth(LEAVE_URL);
// Generic gateway-origin authenticated client (token + silent refresh), for
// features that call the gateway directly with their own /api/v1/* path
// (e.g. the tenant scheduler surface).
export const gatewayApi = withAuth(LEAVE_URL);
// Benefits Service routes through the same gateway as leave (/api/v1/benefits/*).
const benefitsApi = withAuth(LEAVE_URL);
const authApi = withAuth(import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000');
const aiApi = withAuth(import.meta.env.VITE_AI_URL || 'http://localhost:8001');

// Company logo: upload to object storage (employee-service) then persist the URL
// on the tenant (auth-service). Returns the public URL.
export const uploadTenantLogo = (file: File): Promise<string> => {
  const form = new FormData();
  form.append('file', file);
  return employeeApi.post<{ url: string }>('/api/v1/tenant/logo', form).then((r) => r.data.url);
};
export const saveTenantLogo = (logoUrl: string) =>
  authApi.put('/api/v1/tenants/logo', { logoUrl }).then((r) => r.data);
// Mirror the logo onto payroll so payslip PDFs embed it (best-effort).
export const syncPayrollBrandingLogo = (logoUrl: string) =>
  employeeApi.put('/api/v1/payroll/branding', { logo_url: logoUrl }).then((r) => r.data);

// ---- Account / security (auth-service) ----
export const changePassword = (currentPassword: string, newPassword: string) =>
  authApi.post('/api/v1/auth/change-password', { currentPassword, newPassword }).then((r) => r.data);

// ---- Two-factor authentication (self-service, auth-service) ----
export interface MfaStatus { enabled: boolean; backupCodesRemaining: number }
export const getMfaStatus = () =>
  authApi.get<MfaStatus>('/api/v1/auth/mfa/status').then((r) => r.data);
export const mfaSetup = () =>
  authApi.post<{ secret: string; qrCode: string }>('/api/v1/auth/mfa/setup', {}).then((r) => r.data);
export const mfaEnable = (code: string) =>
  authApi.post<{ success: boolean; backupCodes: string[] }>('/api/v1/auth/mfa/enable', { code }).then((r) => r.data);
export const mfaDisable = (password: string) =>
  authApi.post('/api/v1/auth/mfa/disable', { password }).then((r) => r.data);
export const mfaRegenerateCodes = () =>
  authApi.post<{ backupCodes: string[] }>('/api/v1/auth/mfa/backup-codes', {}).then((r) => r.data);
/** Admin/break-glass: reset (disable) another user's 2FA. Requires
 *  `user:mfa:reset`; tenant admins are scoped to their own tenant server-side. */
export const adminResetUserMfa = (userId: string) =>
  authApi.post<{ success: boolean; email: string }>(`/api/v1/auth/mfa/admin/reset/${userId}`, {}).then((r) => r.data);

// ---- Organization / tenant settings (auth-service, tenant_admin) ----
export interface TenantSettings {
  notifications?: Record<string, boolean>;
  security?: Record<string, any>;
  access?: Record<string, boolean>;
  [k: string]: any;
}
export interface TenantDetails {
  id: string;
  name: string;
  displayName?: string | null;
  slug: string;
  domain?: string | null;
  timezone?: string;
  locale?: string;
  defaultCurrency?: string;
  dateFormat?: string | null;
  timeFormat?: string | null;
  primaryColor?: string | null;
  secondaryColor?: string | null;
  logoUrl?: string | null;
  settings?: TenantSettings;
  [k: string]: any;
}
export const getTenant = (id: string) =>
  authApi.get<TenantDetails>(`/api/v1/tenants/${id}`).then((r) => r.data);
// The caller's own tenant from the token — avoids the stale-tenant-id 403 that
// GET /tenants/:id hit when the client's stored tenant id drifted from the session.
export const getCurrentTenant = () =>
  authApi.get<TenantDetails>('/api/v1/tenants/current').then((r) => r.data);

// Only scalar columns the Prisma Tenant model accepts (no `description`).
export const updateTenant = (id: string, data: Partial<TenantDetails>) =>
  authApi.put<TenantDetails>(`/api/v1/tenants/${id}`, data).then((r) => r.data);

// Current user's tenant feature entitlements (any authenticated user).
export const getMyTenantFeatures = () =>
  authApi
    .get<{ features: { featureCode: string; isEnabled: boolean }[] }>('/api/v1/tenants/me/features')
    .then((r) => r.data.features);

// Enable/disable a tenant feature (add-on). Server requires feature-flag:manage
// (owner/tenant_admin only). tenantId is the caller's own tenant.
export const setTenantFeature = (tenantId: string, featureCode: string, isEnabled: boolean) =>
  authApi
    .put<{ featureCode: string; isEnabled: boolean }>(`/api/v1/tenants/${tenantId}/features`, { featureCode, isEnabled })
    .then((r) => r.data);

// ---- Users & access (auth-service, tenant_owner; tenant-scoped server-side) ----
export interface AdminUserCustomRole {
  id: string;
  key: string;
  label: string;
}
export interface AdminUser {
  id: string;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  roles: string[]; // base access ("User Type")
  // Tenant CUSTOM roles assigned via RBAC (e.g. Chairman) — distinct from the
  // base User Type. Batched onto the users list by auth-service.
  customRoles?: AdminUserCustomRole[];
  isActive?: boolean;
  mfaEnabled?: boolean;
  lastLoginAt?: string | null;
  createdAt?: string;
  lockedUntil?: string | null; // set while a login lockout is active
  [k: string]: any;
}
export interface UsersPage {
  users: AdminUser[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

export const listUsers = (params?: {
  page?: number;
  limit?: number;
  search?: string;
  status?: 'active' | 'inactive' | 'locked';
}) => authApi.get<UsersPage>('/api/v1/users', { params }).then((r) => r.data);

/** Replace a user's roles (canonical names). Server stores them canonically. */
export const updateUserRoles = (id: string, roles: string[]) =>
  authApi.put(`/api/v1/users/${id}/role`, { roles }).then((r) => r.data);

/** The current user's own recent activity (curated audit entries: sign-ins, etc.). */
export interface MyActivityEntry {
  id: string;
  action: string;
  method?: string | null;
  endpoint?: string | null;
  entityType?: string | null;
  entityName?: string | null;
  metadata?: Record<string, any> | null;
  timestamp: string;
}
export const getMyActivity = (limit = 15) =>
  authApi
    .get<{ logs: MyActivityEntry[] }>('/api/v1/audit-logs/me', { params: { limit } })
    .then((r) => r.data.logs || []);

export const setUserActive = (id: string, active: boolean) =>
  authApi.put(`/api/v1/users/${id}/${active ? 'activate' : 'deactivate'}`).then((r) => r.data);

/** Clear a login lockout (from repeated failed sign-ins) so the user can try
 *  again immediately, instead of waiting out the cool-off. Requires
 *  `user:deactivate`; tenant admins are scoped to their own tenant server-side. */
export const adminUnlockUser = (id: string) =>
  authApi.put(`/api/v1/users/${id}/unlock`).then((r) => r.data);

// Invite a new user (auth-service invitations). `role` is the invitation DTO
// enum: 'admin' | 'manager' | 'employee' | 'hr' (stored, normalized on read).
export interface InviteUserInput {
  email: string;
  name?: string;
  role: 'admin' | 'manager' | 'employee' | 'hr' | 'finance';
  department?: string;
}
export const inviteUser = (data: InviteUserInput) =>
  authApi.post('/api/v1/invitations/send', { sendWelcomeEmail: true, ...data }).then((r) => r.data);
export interface PendingInvitation {
  id: string;
  email: string;
  name?: string | null;
  role?: string | null;
  createdAt?: string | null;
  expiresAt?: string | null;
  acceptedAt?: string | null;
  invitationToken?: string | null; // admins may share the accept link directly
  metadata?: { status?: string; resentAt?: string } | null;
}
export const listInvitations = () =>
  authApi.get<PendingInvitation[]>('/api/v1/invitations').then((r) => r.data);
export const resendInvitation = (invitationId: string) =>
  authApi.post('/api/v1/invitations/resend', { invitationId }).then((r) => r.data);
export const cancelInvitation = (invitationId: string) =>
  authApi.delete(`/api/v1/invitations/${invitationId}`).then((r) => r.data);

// ---- Types (subset of the backend EmployeeResponse) ----
export type EmploymentType =
  | 'full_time' | 'part_time' | 'casual' | 'contract' | 'fixed_term'
  | 'specific_task' | 'intern' | 'seasonal' | 'consultant' | 'temporary' | 'other';
export type WorkerClassification = 'employee' | 'contractor' | 'casual_worker' | 'temporary_worker';
// Normalized employment dimensions (WCF refactor). work_schedule never determines
// a statutory category; contract_type drives the TZ WCF employment category.
export type WorkSchedule = 'full_time' | 'part_time';
export type ContractType = 'permanent' | 'fixed_term' | 'specific_task';
export type IdentityVerificationStatus = 'missing' | 'pending' | 'verified' | 'rejected' | 'not_applicable';
export type WorkLocationType = 'office' | 'field' | 'remote' | 'hybrid' | 'client_site';

export interface Employee {
  id: string;
  tenant_id?: string;
  user_id?: string | null;
  employee_id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string | null;
  department_id?: string | null;
  position?: string;
  // Reporting-hierarchy level (separate from auth permission role).
  org_level?: 'employee' | 'manager' | 'director' | 'executive' | 'ceo' | string;
  hire_date?: string;
  status?: string;
  manager_id?: string | null;
  profile_picture_url?: string | null;
  nationality?: string | null;
  marital_status?: string | null;
  national_id?: string | null;
  nssf?: string | null;
  tin?: string | null;
  driver_license?: string | null;
  dob?: string | null;
  // ---- Master data (Increment B3) — all optional; PUT accepts any subset ----
  middle_name?: string | null;
  preferred_name?: string | null;
  /** Read-only, generated server-side from first/middle/last. */
  full_legal_name?: string | null;
  gender?: 'male' | 'female' | 'other' | 'prefer_not_to_say' | string | null;
  scanner_employee_id?: string | null;
  badge_number?: string | null;
  legacy_employee_id?: string | null;
  passport_number?: string | null;
  work_permit_number?: string | null;
  resident_permit_number?: string | null;
  identity_verification_status?: IdentityVerificationStatus | string | null;
  wcf_number?: string | null;
  alternate_phone?: string | null;
  personal_email?: string | null;
  employment_type?: EmploymentType | string | null;
  worker_classification?: WorkerClassification | string | null;
  work_schedule?: WorkSchedule | string | null;
  contract_type?: ContractType | string | null;
  confirmation_date?: string | null;
  probation_end_date?: string | null;
  contract_start_date?: string | null;
  contract_end_date?: string | null;
  termination_date?: string | null;
  termination_reason?: string | null;
  rehire_eligible?: boolean | null;
  branch_id?: string | null;
  job_title_id?: string | null;
  cost_center?: string | null;
  project?: string | null;
  work_location_type?: WorkLocationType | string | null;
  [k: string]: any;
}

export interface Paginated<T> {
  items: T[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

// ---- Employee (self) ----
// Stale-while-revalidate cache: pages can render the last-known profile
// instantly on reload, then update when the fresh response lands.
const PROFILE_CACHE_KEY = 'hr_profile';
let _profileMem: Employee | null = null;

export function getCachedProfile(): Employee | null {
  if (_profileMem) return _profileMem;
  try {
    const s = sessionStorage.getItem(PROFILE_CACHE_KEY);
    if (s) { _profileMem = JSON.parse(s); return _profileMem; }
  } catch { /* ignore */ }
  return null;
}

// The cached profile belongs to ONE identity. Session hydration calls this when
// the cookie session's user differs from the persisted one, so a tab can never
// keep rendering the previous principal's profile.
export function clearCachedProfile(): void {
  _profileMem = null;
  try { sessionStorage.removeItem(PROFILE_CACHE_KEY); } catch { /* ignore */ }
}

// Single-flight: many components load the current profile independently
// (header, dashboard, personal page, Dana…). Share one in-flight request so a
// page render issues at most one GET /employees/me/profile.
let _profileInflight: Promise<Employee> | null = null;

export const getMyProfile = () => {
  if (_profileInflight) return _profileInflight;
  _profileInflight = employeeApi
    .get<Employee>('/api/v1/employees/me/profile')
    .then((r) => {
      _profileMem = r.data;
      try { sessionStorage.setItem(PROFILE_CACHE_KEY, JSON.stringify(r.data)); } catch { /* quota */ }
      return r.data;
    })
    .finally(() => { _profileInflight = null; });
  return _profileInflight;
};

export const updateEmployee = (id: string, data: Partial<Employee>) =>
  employeeApi.put<Employee>(`/api/v1/employees/${id}`, data).then((r) => r.data);

// Create / delete employees (employee-service, requires employee:write / :delete).
export interface EmployeeCreateInput {
  employee_id: string;
  first_name: string;
  middle_name?: string;
  last_name: string;
  preferred_name?: string;
  email: string;
  position: string;
  hire_date: string; // YYYY-MM-DD
  phone?: string;
  department_id?: string | null;
  org_level?: string;
  /** 'Draft' starts the new hire in the onboarding lifecycle (payroll-blocked
   * until the checklist completes); omit for the legacy immediate-Active path. */
  status?: string;
}
export const createEmployee = (data: EmployeeCreateInput) =>
  employeeApi.post<Employee>('/api/v1/employees/', data).then((r) => r.data);
export const deleteEmployee = (id: string) =>
  employeeApi.delete(`/api/v1/employees/${id}`).then((r) => r.data);
// Hard delete — duplicate/mistaken records ONLY (tenant owner). The backend
// refuses records linked to a login account (use offboarding for real exits).
//
// `unlinkLogin` is the duplicate-resolution path: a duplicate that was ever
// invited HAS a login, so the ordinary guard refuses it permanently. This
// retires the account first and then deletes the record — the server still
// refuses unless it independently agrees the record duplicates another, so
// this flag cannot be pointed at a real employee.
export const deleteEmployeeRecord = (id: string, unlinkLogin = false) =>
  employeeApi
    .delete(`/api/v1/employees/${id}?soft_delete=false${unlinkLogin ? '&unlink_login=true' : ''}`)
    .then((r) => r.data);

// ---- Directory ----
export const getEmployees = (params?: { page?: number; page_size?: number; search?: string; department_id?: string; status?: string }) => {
  // Backend expects the text term as `query`; keep `search` ergonomic here.
  const { search, ...rest } = params || {};
  return employeeApi
    .get<Paginated<Employee>>('/api/v1/employees/', { params: { ...rest, ...(search ? { query: search } : {}) } })
    .then((r) => r.data);
};

export const getEmployee = (id: string) =>
  employeeApi.get<Employee>(`/api/v1/employees/${id}`).then((r) => r.data);

// Fetch every employee in the tenant (paginates through all pages). Used by
// views that need the whole directory (org chart, hierarchy).
export const getAllEmployees = async (): Promise<Employee[]> => {
  const out: Employee[] = [];
  let page = 1;
  const pageSize = 100;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const data = await getEmployees({ page, page_size: pageSize });
    out.push(...(data.items || []));
    const totalPages = data.total_pages ?? 1;
    if (page >= totalPages || (data.items || []).length < pageSize) break;
    page++;
  }
  return out;
};

export interface DepartmentSummary {
  department_count: number;
  active_employee_count: number;   // active employees across the whole tenant
  unassigned_employee_count: number;
}
export const getDepartments = () =>
  employeeApi
    .get<Paginated<any> & { summary?: DepartmentSummary }>('/api/v1/departments/', { params: { page: 1, page_size: 100 } })
    .then((r) => r.data);

// Department create/edit (employee-service, require_manager — tenant admins/HR).
export interface DepartmentInput {
  name: string;
  description?: string | null;
  manager_id?: string | null;
  is_active?: boolean;
}
export const createDepartment = (data: DepartmentInput) =>
  employeeApi.post('/api/v1/departments/', data).then((r) => r.data);
export const updateDepartment = (id: string, data: Partial<DepartmentInput>) =>
  employeeApi.put(`/api/v1/departments/${id}`, data).then((r) => r.data);
export const deleteDepartment = (id: string, force = false) =>
  employeeApi.delete(`/api/v1/departments/${id}${force ? '?force=true' : ''}`).then((r) => r.data);

// ---- Job titles (employee-service) — admin-defined catalog. Employees keep
// their title as the free-text `position` string; this is the suggested list. ----
export interface JobTitle {
  id: string;
  name: string;
  description?: string | null;
  is_active?: boolean;
  employee_count?: number | null;
}
export interface JobTitleInput {
  name: string;
  description?: string | null;
  is_active?: boolean;
}
export const getJobTitles = () =>
  employeeApi.get<Paginated<JobTitle>>('/api/v1/job-titles/', { params: { page: 1, page_size: 100 } }).then((r) => r.data);
export const createJobTitle = (data: JobTitleInput) =>
  employeeApi.post('/api/v1/job-titles/', data).then((r) => r.data);
export const updateJobTitle = (id: string, data: Partial<JobTitleInput>) =>
  employeeApi.put(`/api/v1/job-titles/${id}`, data).then((r) => r.data);
export const deleteJobTitle = (id: string) =>
  employeeApi.delete(`/api/v1/job-titles/${id}`).then((r) => r.data);

// ---- Branches / work sites (employee-service) ----
export interface Branch {
  id: string;
  name: string;
  code?: string | null;
  location?: string | null;
  is_active?: boolean;
  employee_count?: number | null;
}
export const listBranches = () =>
  employeeApi.get<Paginated<Branch>>('/api/v1/branches/', { params: { page: 1, page_size: 100 } }).then((r) => r.data.items || []);
export const createBranch = (data: { name: string; code?: string; location?: string }) =>
  employeeApi.post<Branch>('/api/v1/branches/', data).then((r) => r.data);
export const updateBranch = (id: string, data: Partial<{ name: string; code?: string; location?: string }>) =>
  employeeApi.put<Branch>(`/api/v1/branches/${id}`, data).then((r) => r.data);
// 409 while employees are still assigned to the branch.
export const deleteBranch = (id: string) =>
  employeeApi.delete(`/api/v1/branches/${id}`).then((r) => r.data);

// ---- Data readiness (employee-service) — payroll/master-data completeness ----
export interface ReadinessBucket {
  passed: number;
  total: number;
  score: number;
  missing: { code: string; label: string }[];
}
export interface EmployeeReadiness {
  employee_id: string;
  score: number; // 0-100
  buckets: Partial<Record<'profile' | 'statutory' | 'attendance' | 'contact' | 'documents', ReadinessBucket>>;
  duplicates: string[];
  missing_count: number;
}
export const getEmployeeReadiness = (id: string) =>
  employeeApi.get<EmployeeReadiness>(`/api/v1/employees/${id}/readiness`).then((r) => r.data);

export interface ReadinessSummary {
  counts: {
    total: number;
    complete: number;
    missing_tin: number;
    missing_nssf: number;
    missing_national_id: number;
    missing_scanner_id: number;
    missing_phone: number;
    missing_employment_type: number;
    missing_contract?: number;
    missing_documents?: number;
    with_duplicates: number;
  };
  average_score: number;
  duplicates: Record<string, string[]>;
  least_complete: { employee_id: string; name: string; score: number; missing_count: number; duplicates: string[] }[];
}
export const getReadinessSummary = () =>
  employeeApi.get<ReadinessSummary>('/api/v1/employees/readiness/summary').then((r) => r.data);

// ---- Leave policies (leave-service, HR/admin) ----
export interface LeavePolicy {
  id: string;
  leave_type_id: string;
  name: string;
  /** Multi-entity G5: a company's own policy (null = every company). */
  legal_entity_id?: string | null;
  type_code?: string | null;
  type_name?: string | null;
  annual_entitlement: number;
  accrual_frequency: string;
  /** How the tenant TRACKS this type. Stored on the leave type, edited with
   *  the policy because to an administrator it is one decision.
   *    entitlement   — fixed allowance, granted up front, refused when spent
   *    accrual       — earned per the frequency, refused beyond what is earned
   *    informational — recorded only, never refused */
  balance_category?: 'entitlement' | 'accrual' | 'informational' | null;
  /** Exempt from the tenant's "arrange cover before you go" rule. Exempt never
   *  means delegation is unavailable — only that it is not demanded. */
  delegation_exempt?: boolean | null;
  /** How delegation works for this TYPE: policy (follow the company rule) |
   *  optional (never required — Sick) | manager (manager arranges cover) |
   *  exempt (still working — WFH; same as delegation_exempt). */
  delegation_mode?: 'policy' | 'optional' | 'manager' | 'exempt' | null;
  carry_forward_allowed?: boolean;
  max_carry_forward?: number | null;
  prorate_on_join?: boolean;
  prorate_on_exit?: boolean;
  notice_required_days?: number;
  max_consecutive_days?: number | null;
  min_service_days?: number;
  is_active?: boolean;
  [k: string]: any;
}
// Configurable leave policies (CRUD endpoint). Distinct from the older
// getLeavePolicies(/leaves/policies) summary; ids here match PUT/DELETE below.
export const listLeavePolicies = () =>
  leaveApi.get<LeavePolicy[]>('/api/v1/leave-policies/').then((r) => r.data);
// POST validates against LeavePolicyCreate — requires leave_type_id, name,
// annual_entitlement, accrual_frequency.
export const createLeavePolicy = (policy: Partial<LeavePolicy>) =>
  leaveApi.post('/api/v1/leave-policies/', policy).then((r) => r.data);
// PUT validates against LeavePolicyBase (required fields) — send the full policy.
export const updateLeavePolicy = (id: string, policy: LeavePolicy) =>
  leaveApi.put(`/api/v1/leave-policies/${id}`, policy).then((r) => r.data);
// ⚠️ A SOFT DELETE: the row survives with is_active=false. Nothing is lost,
// but until there was a way back an accidental delete could only be undone
// with SQL against production (Fern, 2026-10-12).
export const deleteLeavePolicy = (id: string) =>
  leaveApi.delete(`/api/v1/leave-policies/${id}`).then((r) => r.data);
// The deleted ones, so an admin can see what they removed and put it back.
export const listDeletedLeavePolicies = () =>
  leaveApi
    .get<LeavePolicy[]>('/api/v1/leave-policies/', { params: { is_active: false } })
    .then((r) => r.data);
// Restores the ORIGINAL row, keeping its entitlement and settings — which is
// the point of restoring rather than re-creating. 409 when an active policy
// already holds the name.
export const restoreLeavePolicy = (id: string) =>
  leaveApi.post(`/api/v1/leave-policies/${id}/restore`).then((r) => r.data);

// ---- Reporting hierarchy (org level) ----
export interface OrgSummary {
  employee_id: string;
  direct_reports: number;
  total_reports: number;
  by_level: Record<string, number>;
  by_status: Record<string, number>;
}
export const getMySubordinates = (recursive = true) =>
  employeeApi.get<Employee[]>('/api/v1/employees/me/subordinates', { params: { recursive } }).then((r) => r.data);
export const getMyOrgSummary = () =>
  employeeApi.get<OrgSummary>('/api/v1/employees/me/org-summary').then((r) => r.data);
export const getSubordinates = (employeeId: string, recursive = true) =>
  employeeApi.get<Employee[]>(`/api/v1/employees/${employeeId}/subordinates`, { params: { recursive } }).then((r) => r.data);
export const getOrgSummary = (employeeId: string) =>
  employeeApi.get<OrgSummary>(`/api/v1/employees/${employeeId}/org-summary`).then((r) => r.data);

// Leave balances for everyone in my org (by user id). Manager/admin only.
export interface OrgEmployeeBalances {
  employee_id: string; // == auth user id
  balances: { type: string; type_code: string; entitled: number; taken: number; pending: number; available: number }[];
  total_entitled: number;
  total_taken: number;
  total_pending: number;
  total_available: number;
}
export const getOrgBalances = (employeeIds: string[], year?: number) =>
  leaveApi
    .post<OrgEmployeeBalances[]>('/api/v1/leaves/org-balances', { employee_ids: employeeIds, year })
    .then((r) => r.data);

// ---- AI assistant (ai-agent-service; per-tenant Claude + org tools) ----
export interface AiChatTurn { role: 'user' | 'assistant'; content: string; }
export interface AiChatResponse { reply: string; tools_used: string[]; configured: boolean; conversation_id?: string; }
/** One screen the current user can reach — resolved from the nav registry
 *  (RBAC + entitlement filtered), so Dana's guidance stays accurate & current. */
export interface AiNavPage { label: string; path: string; group: string }
/** Current in-app context so Dana can give context-aware navigation help. */
export interface AiChatContext {
  page?: string; path?: string; role?: string; navigation?: AiNavPage[];
  /** The device Dana is answering on (install/update guidance) — see danaClientContext. */
  client?: import('@/features/shared/components/install/danaClientContext').DanaClientContext;
}
export const aiChat = (message: string, history: AiChatTurn[] = [], context?: AiChatContext, conversationId?: string) =>
  aiApi.post<AiChatResponse>('/api/v1/ai/chat', { message, history, context, conversation_id: conversationId }).then((r) => r.data);

// Server-side Dana chat history (short-lived, self-scoped) — restore the thread
// on widget mount so it survives reloads/devices.
export interface DanaConversation { conversation_id: string | null; messages: AiChatTurn[]; retention_days?: number }
export const getDanaConversation = (conversationId?: string) =>
  aiApi
    .get<DanaConversation>('/api/v1/ai/conversations/current', { params: conversationId ? { conversation_id: conversationId } : undefined })
    .then((r) => r.data)
    .catch(() => ({ conversation_id: null, messages: [] as AiChatTurn[] }));
export const deleteDanaConversation = (conversationId: string) =>
  aiApi.delete(`/api/v1/ai/conversations/${conversationId}`).then((r) => r.data).catch(() => ({ deleted: 0 }));

// OCR a timesheet photo/PDF into {headers, rows} of per-employee hours (Dana vision).
export const extractTimesheet = (body: { image_base64: string; media_type: string; hint?: string }) =>
  aiApi
    .post<{ headers: string[]; rows: string[][] }>('/api/v1/ai/extract-timesheet', body)
    .then((r) => r.data);

// Company HR policy documents (read-only for employees; managed by admins in
// the admin portal). Tenant-scoped — never another company's docs.
export interface CompanyDoc {
  id: string; name: string; content_type?: string | null;
  category?: string | null; // Legal|Payroll|Benefits|HR|Tax — null on legacy docs
  chunk_count: number; status: string; created_at?: string | null;
}
export const getCompanyDocuments = () =>
  aiApi.get<CompanyDoc[]>('/api/v1/ai/documents').then((r) => r.data);
export const downloadCompanyDocument = (id: string) =>
  aiApi.get(`/api/v1/ai/documents/${id}/download`, { responseType: 'blob' }).then((r) => r.data as Blob);

// ---- Profile sub-resources (add/save when missing) ----
export const getAddresses = (employeeId: string) =>
  employeeApi.get<any[]>(`/api/v1/employees/${employeeId}/addresses`).then((r) => r.data);
export const addAddress = (employeeId: string, data: any) =>
  employeeApi.post(`/api/v1/employees/${employeeId}/addresses`, data).then((r) => r.data);

export const getEmergencyContacts = (employeeId: string) =>
  employeeApi.get<any[]>(`/api/v1/employees/${employeeId}/emergency-contacts`).then((r) => r.data);
export const addEmergencyContact = (employeeId: string, data: any) =>
  employeeApi.post(`/api/v1/employees/${employeeId}/emergency-contacts`, data).then((r) => r.data);

export const getEducation = (employeeId: string) =>
  employeeApi.get<any[]>(`/api/v1/employees/${employeeId}/education`).then((r) => r.data);
export const addEducation = (employeeId: string, data: any) =>
  employeeApi.post(`/api/v1/employees/${employeeId}/education`, data).then((r) => r.data);

// ---- Sub-resource edit / delete (self-service personal data) ----
export const updateAddress = (employeeId: string, addressId: string, data: any) =>
  employeeApi.put(`/api/v1/employees/${employeeId}/addresses/${addressId}`, data).then((r) => r.data);
export const deleteAddress = (employeeId: string, addressId: string) =>
  employeeApi.delete(`/api/v1/employees/${employeeId}/addresses/${addressId}`).then((r) => r.data);
export const updateEmergencyContact = (employeeId: string, contactId: string, data: any) =>
  employeeApi.put(`/api/v1/employees/${employeeId}/emergency-contacts/${contactId}`, data).then((r) => r.data);
export const deleteEmergencyContact = (employeeId: string, contactId: string) =>
  employeeApi.delete(`/api/v1/employees/${employeeId}/emergency-contacts/${contactId}`).then((r) => r.data);
export const updateEducation = (employeeId: string, educationId: string, data: any) =>
  employeeApi.put(`/api/v1/employees/${employeeId}/education/${educationId}`, data).then((r) => r.data);
export const deleteEducation = (employeeId: string, educationId: string) =>
  employeeApi.delete(`/api/v1/employees/${employeeId}/education/${educationId}`).then((r) => r.data);

// ---- Employee payment methods (payout destination for payroll) ----
// Account/phone numbers are WRITE-ONLY: the backend only ever returns the
// masked forms (account_masked / phone_masked), never the full value.
export type PaymentMethodType = 'BANK' | 'MOBILE_MONEY';
export type PaymentMethodStatus =
  | 'PENDING_VERIFICATION'
  | 'APPROVED'
  | 'REJECTED'
  | 'INACTIVE';

export interface PaymentMethod {
  id: string;
  employee_id: string;
  type: PaymentMethodType;
  bank_name?: string;
  branch?: string;
  account_holder_name?: string;
  account_masked?: string;
  mobile_money_provider?: string;
  phone_masked?: string;
  country?: string;
  currency?: string;
  /** Preferred method WITHIN its type (one bank + one wallet may both be preferred). */
  is_primary: boolean;
  /** THE destination payroll pays — at most one per employee, always approved. */
  is_default?: boolean;
  status: PaymentMethodStatus;
  rejection_reason?: string;
  /** PM11: this method WAS the salary destination when it was deactivated,
   *  edited or rejected; while no new default is chosen, salary is on hold. */
  default_released_at?: string | null;
  default_released_reason?: string | null;
  approved_at?: string;
  created_at?: string;
  updated_at?: string;
}

export interface PaymentMethodCreate {
  type: PaymentMethodType;
  is_primary?: boolean;
  country?: string;
  currency?: string;
  bank_name?: string;
  branch?: string;
  account_holder_name?: string;
  account_number?: string; // write-only
  mobile_money_provider?: string;
  phone_number?: string; // write-only
}

export interface PaymentMethodAudit {
  id: string;
  action: string;
  actor_id?: string;
  detail?: Record<string, unknown>;
  created_at?: string;
}

// Self-service (the logged-in employee)
export const getMyPaymentMethods = () =>
  employeeApi.get<PaymentMethod[]>('/api/v1/employees/me/payment-methods').then((r) => r.data);
export const addMyPaymentMethod = (data: PaymentMethodCreate) =>
  employeeApi.post<PaymentMethod>('/api/v1/employees/me/payment-methods', data).then((r) => r.data);
export const updateMyPaymentMethod = (id: string, data: Partial<PaymentMethodCreate>) =>
  employeeApi.patch<PaymentMethod>(`/api/v1/employees/me/payment-methods/${id}`, data).then((r) => r.data);
export const deactivateMyPaymentMethod = (id: string) =>
  employeeApi.delete<PaymentMethod>(`/api/v1/employees/me/payment-methods/${id}`).then((r) => r.data);
export const setPrimaryPaymentMethod = (id: string) =>
  employeeApi.post<PaymentMethod>(`/api/v1/employees/me/payment-methods/${id}/set-primary`).then((r) => r.data);
// Make an approved method THE payroll destination (one default per employee).
export const setMyDefaultPaymentMethod = (id: string) =>
  employeeApi.post<PaymentMethod>(`/api/v1/employees/me/payment-methods/${id}/set-default`).then((r) => r.data);

// HR/Finance + audit
export const getEmployeePaymentMethods = (employeeId: string) =>
  employeeApi.get<PaymentMethod[]>(`/api/v1/employees/${employeeId}/payment-methods`).then((r) => r.data);
// HR/Finance add a payout method ON BEHALF of an employee — created + approved in
// one step (the caller is already an approver). Needs employee:payment:approve.
export const addEmployeePaymentMethod = (employeeId: string, data: PaymentMethodCreate) =>
  employeeApi.post<PaymentMethod>(`/api/v1/employees/${employeeId}/payment-methods`, data).then((r) => r.data);
// HR/Finance switch an employee's default between their approved methods
// (employee:payment:approve).
// HR/Finance deactivate (kept as history; never deleted). If it was the salary
// destination, salary is held until a new default is chosen (PM11).
export const deactivateEmployeePaymentMethod = (employeeId: string, id: string) =>
  employeeApi
    .delete<PaymentMethod>(`/api/v1/employees/${employeeId}/payment-methods/${id}`)
    .then((r) => r.data);
export const setEmployeeDefaultPaymentMethod = (employeeId: string, id: string) =>
  employeeApi
    .post<PaymentMethod>(`/api/v1/employees/${employeeId}/payment-methods/${id}/set-default`)
    .then((r) => r.data);
export const getPaymentMethodAudit = (employeeId: string, id: string) =>
  employeeApi
    .get<PaymentMethodAudit[]>(`/api/v1/employees/${employeeId}/payment-methods/${id}/audit`)
    .then((r) => r.data);

// Approvals (needs permission employee:change:approve)
export const getPendingPaymentMethods = () =>
  employeeApi.get<PaymentMethod[]>('/api/v1/payment-methods/pending').then((r) => r.data);
export const approvePaymentMethod = (id: string) =>
  employeeApi.post<PaymentMethod>(`/api/v1/payment-methods/${id}/approve`).then((r) => r.data);
export const rejectPaymentMethod = (id: string, reason: string) =>
  employeeApi.post<PaymentMethod>(`/api/v1/payment-methods/${id}/reject`, { reason }).then((r) => r.data);

export const getDocuments = (employeeId: string) =>
  employeeApi.get<any[]>(`/api/v1/documents/${employeeId}`).then((r) => r.data);

// Upload an employee document to private object storage (returns the row, incl.
// a short-lived download_url). document_type: id|passport|driver_license|degree|
// certificate|contract|other
export const uploadDocument = (
  employeeId: string,
  file: File,
  documentType = 'other',
  meta?: { issuedAt?: string; expiresAt?: string; documentNumber?: string; replacesId?: string },
) => {
  const form = new FormData();
  form.append('file', file);
  const q = new URLSearchParams({ document_type: documentType });
  if (meta?.issuedAt) q.set('issued_at', meta.issuedAt);
  if (meta?.expiresAt) q.set('expires_at', meta.expiresAt);
  if (meta?.documentNumber) q.set('document_number', meta.documentNumber);
  if (meta?.replacesId) q.set('replaces_id', meta.replacesId);
  return employeeApi
    .post(`/api/v1/documents/${employeeId}/upload?${q.toString()}`, form)
    .then((r) => r.data);
};

// --- compliance program (document verification + workforce compliance) ------
export interface RequirementStatus {
  type: string; label: string;
  status: 'MISSING' | 'UPLOADED' | 'PENDING_VERIFICATION' | 'VERIFIED' | 'REJECTED' | 'EXPIRING_SOON' | 'EXPIRED';
  documentId?: string | null; expiresAt?: string | null; rejectionReason?: string | null;
  hrVerificationRequired?: boolean; expirationRequired?: boolean;
}

export const getPendingDocumentVerifications = () =>
  employeeApi.get<{ documents: any[] }>('/api/v1/documents/verification/pending').then((r) => r.data.documents ?? []);

export const verifyDocument = (employeeId: string, documentId: string) =>
  employeeApi.post(`/api/v1/documents/${employeeId}/${documentId}/verify`).then((r) => r.data);

export const rejectDocument = (employeeId: string, documentId: string, reason: string) =>
  employeeApi.post(`/api/v1/documents/${employeeId}/${documentId}/reject`, { reason }).then((r) => r.data);

export const getDocumentComplianceSummary = (params?: Record<string, string | number>) =>
  employeeApi.get('/api/v1/employees/document-compliance/summary', { params }).then((r) => r.data);

export const getMyChangeRequests = () =>
  employeeApi.get<{ requests: any[] }>('/api/v1/change-requests/mine').then((r) => r.data.requests ?? []);

export const deleteDocument = (employeeId: string, documentId: string) =>
  employeeApi.delete(`/api/v1/documents/${employeeId}/${documentId}`).then((r) => r.data);

// Upload a profile picture to the public avatars bucket; returns { url }.
export const uploadProfilePicture = (employeeId: string, file: Blob, filename = 'avatar.jpg') => {
  const form = new FormData();
  form.append('file', file, filename);
  return employeeApi
    .post<{ url: string }>(`/api/v1/employees/${employeeId}/profile-picture`, form)
    .then((r) => r.data);
};

// ---- Analytics (dashboard) ----
export const getDashboardAnalytics = () =>
  employeeApi.get<any>('/api/v1/analytics/dashboard').then((r) => r.data);

// ---- Leave ----
export const getLeaveBalances = () =>
  leaveApi.get<any[]>('/api/v1/leaves/balances').then((r) => r.data);
export const getMyLeaveRequests = () =>
  leaveApi.get<any[]>('/api/v1/leave-requests/').then((r) => r.data);
/** Cancel (or withdraw) a leave request — a reason is required. The employee
 *  may cancel their own approved leave until it starts; after that only their
 *  manager/HR may (the server refuses with a message that says so). */
export const cancelLeaveRequest = (id: string, reason: string) =>
  leaveApi.post(`/api/v1/leave-requests/${id}/cancel`, { request_id: id, reason }).then((r) => r.data);

// ---- Personal events / meetings (also created by Dana) ----
export interface EventAttendee { email: string; name?: string; status?: string; role?: 'participant' | 'co_host' }
export interface MyEvent {
  id: string;
  title: string;
  description?: string;
  location?: string;
  start_at: string;
  end_at: string;
  status: string;
  source: string;
  /** organizer = hosts + manages; creator = scheduled it (delegation). */
  role?: 'organizer' | 'creator' | 'attendee';
  organizer_user_id?: string;
  created_by?: string;
  attendees?: EventAttendee[];
  synced_to_calendar: boolean;
}
export const listMyEvents = (params?: { from?: string; to?: string; include_past?: boolean }) =>
  leaveApi.get<MyEvent[]>('/api/v1/events/', { params }).then((r) => r.data);
export const createMyEvent = (body: {
  title: string; start_at: string; end_at?: string; description?: string; location?: string;
  meeting_timezone?: string; organizer_timezone?: string; // IANA
  attendees?: { email: string; name?: string; role?: string }[];
  /** Delegated scheduling: the internal organizer this meeting belongs to
   * (provider link + calendar ownership follow them). Default: the caller. */
  organizer_email?: string;
  // Ask the connected calendar (Google → Meet, Outlook → Teams) to attach a
  // video link; the join URL becomes the meeting location.
  generate_meet_link?: boolean;
}) => leaveApi.post<MyEvent>('/api/v1/events/', body).then((r) => r.data);
export const getMyEvent = (id: string) =>
  leaveApi.get<MyEvent>(`/api/v1/events/${id}`).then((r) => r.data);
export const updateMyEvent = (id: string, patch: {
  title?: string; start_at?: string; end_at?: string; description?: string; location?: string;
  // Full replacement set: additions are invited, removals get a cancellation
  // notice, kept attendees are untouched. Omit to leave attendees alone.
  attendees?: { email: string; name?: string; role?: string }[];
  /** Organizer transfer (organizer/creator only; audited). */
  organizer_email?: string;
}) => leaveApi.patch<MyEvent>(`/api/v1/events/${id}`, patch).then((r) => r.data);
export const cancelMyEvent = (id: string) =>
  leaveApi.delete(`/api/v1/events/${id}`).then((r) => r.data);

// ---- Unified Workforce Calendar (gateway aggregation across services) ----
// Types live in lib/calendarApi.ts; the endpoint is gateway-local, so any of
// the authed instances reaches it (they all point at the gateway).
export const getCalendarEvents = (params: {
  from: string; to: string; scope?: import('./calendarApi').CalendarScope; types?: string; status?: string;
}) =>
  leaveApi
    .get<import('./calendarApi').CalendarFeed>('/api/v1/calendar/events', { params })
    .then((r) => r.data);

// The create endpoint (SubmitLeaveRequest) expects camelCase dates + a reason.
export interface LeaveRequestCreate {
  type: 'vacation' | 'sick' | 'wfh' | string;
  startDate: string;
  endDate?: string;
  reason: string;
  notes?: string;
  // Half-day leave (single-day requests only); omit for a full day.
  dayType?: 'full' | 'half_morning' | 'half_afternoon';
  /** Multi-entity G5: the employment the leave is taken from (only for someone
   *  employed by more than one company; omitted → their primary employment). */
  engagementId?: string;
  /** R2: a day off from several employments — one part per employment. */
  engagementIds?: string[];
}
export interface LeaveType {
  id: string;
  name: string;
  type_code: string;
  is_paid: boolean;
  description?: string | null;
  /** Type-level settings (also shown on each of the type's policies). */
  balance_category?: LeavePolicy['balance_category'];
  delegation_exempt?: boolean | null;
  delegation_mode?: LeavePolicy['delegation_mode'];
}
// The tenant's configured leave types (data-driven Request Time Off picker).
export const getLeaveTypes = () =>
  leaveApi.get<LeaveType[]>('/api/v1/leave-types/').then((r) => r.data);

/** The signed "nothing needs covering" statement, when the employee gave one.
 *  `signed_at` is NOT sent — the server stamps it. */
export interface CoverageAttestationInput {
  statement: string;
  signedName: string;
}

/** Attach a REPLACEMENT delegation to my pending / approved request after its
 *  cover was declined or cancelled (validated server-side like submission). */
export const replaceLeaveDelegation = (requestId: string, delegationId: string) =>
  leaveApi.put(`/api/v1/leave-requests/${requestId}/delegation`, { delegationId }).then((r) => r.data);

/** An APPROVER arranges (or, with replace, swaps) cover for a report's
 *  request: auth creates a coverage-only delegation (no approvals delegated)
 *  for the employee, and the delegate is asked to accept. Returns the request
 *  with its recomputed coverage_status. 409 COVER_ALREADY_ARRANGED when cover
 *  is still in place and replace is not set. */
export const assignLeaveCover = (
  requestId: string,
  body: { delegateEmployeeId: string; note?: string; replace?: boolean },
) => leaveApi.put(`/api/v1/leave-requests/${requestId}/cover`, body).then((r) => r.data);

export const createLeaveRequest = (data: LeaveRequestCreate & { attachments?: { document_id: string; file_name?: string }[]; delegationId?: string | null; coverageAttestation?: CoverageAttestationInput | null }) =>
  leaveApi.post('/api/v1/leave-requests/', data).then((r) => r.data);

/** Delegation ↔ Leave: does company policy require ME to delegate before taking
 *  leave, and how must the delegate confirm? (evaluated server-side). */
export interface CoverageRequirement {
  required: boolean;
  confirmation: 'not_required' | 'before_submission' | 'before_final_approval';
  cc: { mode: 'optional' | 'required'; autoManager: boolean; autoHr: boolean; allowManual: boolean };
  eligibleDelegates: { rule: string; roles: string[] };
  dateBuffers: { startDaysBefore: number; endDaysAfter: number };
  version: number;
  /** Set when the requirement was lifted because THIS leave type is exempt
   *  (Work From Home — the person is still working). Lets the form say "this
   *  type is exempt" rather than implying the company has no policy. */
  exemptTypeCode?: string;
  /** This leave TYPE's delegation mode (leave policy setting): policy =
   *  follows the tenant rule above; optional = never required; manager = the
   *  manager arranges cover; exempt = still working, nothing to hand over. */
  typeMode?: 'policy' | 'optional' | 'manager' | 'exempt';
}
/** `leaveType` is the UI code (vacation|sick|wfh). Pass it so a
 *  delegation-exempt type answers correctly; omit it before a type is chosen
 *  and the tenant's general answer comes back. The SAME evaluation gates
 *  submission server-side, so the form and the backend cannot disagree. */
export const getCoverageRequirement = (leaveType?: string) =>
  leaveApi
    .get<CoverageRequirement>('/api/v1/leave-requests/coverage-requirement', {
      params: leaveType ? { leave_type: leaveType } : undefined,
    })
    .then((r) => r.data);

/** "Coverage during leave" block on a leave request (people + labels + status). */
export interface LeaveCoverage {
  delegation_id: string;
  delegate_name: string | null;
  delegator_name: string | null;
  status: 'confirmed' | 'pending' | 'declined' | 'cancelled' | 'expired';
  delegation_status: string;
  starts_at: string | null;
  ends_at: string | null;
  responsibilities: string[];
  scope_summary: string;
  cc_count: number;
  note?: string | null;
}
export const getLeaveRequestDetail = (id: string) =>
  leaveApi.get<any & { delegation_id?: string | null; coverage?: LeaveCoverage | null }>(`/api/v1/leave-requests/${id}`).then((r) => r.data);
export const getLeaveEvidenceRules = () =>
  leaveApi.get<{ rules: Record<string, number> }>('/api/v1/leave-requests/evidence-rules').then((r) => r.data.rules ?? {});
export const attachLeaveEvidence = (requestId: string, documentId: string, fileName?: string) =>
  leaveApi.post(`/api/v1/leave-requests/${requestId}/evidence`, { document_id: documentId, file_name: fileName }).then((r) => r.data);
export const getUpcomingLeave = () =>
  leaveApi.get<any[]>('/api/v1/leave-requests/upcoming').then((r) => r.data);

// Who's out: approved leave (employee_id = user id). includePending opts into
// PENDING rows the caller is allowed to see (org-wide approver: all; manager:
// own subtree; anyone: their own) — rows carry status so the UI can badge them.
// scope 'org' (default) is the whole company — the directory's "on leave"
// badges; 'direct' is the caller's immediate direct reports, resolved by the
// server — what any "my team" screen means (v2.1 §3.1).
export const getTeamOut = (daysAhead = 30, includePending = false, scope: 'org' | 'direct' = 'org') =>
  leaveApi.get<any[]>('/api/v1/leave-requests/team', {
    params: {
      days_ahead: daysAhead,
      ...(includePending ? { include_pending: true } : {}),
      ...(scope === 'direct' ? { scope: 'direct' } : {}),
    },
  }).then((r) => r.data);

// Manager approvals
export const getPendingApprovals = () =>
  leaveApi.get<any[]>('/api/v1/leave-requests/pending').then((r) => r.data);
export const approveLeaveRequest = (id: string, comments?: string) =>
  leaveApi.post(`/api/v1/leave-requests/${id}/approve`, { request_id: id, comments }).then((r) => r.data);
export const rejectLeaveRequest = (id: string, reason: string, comments?: string) =>
  leaveApi.post(`/api/v1/leave-requests/${id}/reject`, { request_id: id, reason, comments }).then((r) => r.data);
export const getLeavePolicies = () =>
  leaveApi.get<any[]>('/api/v1/leave-policies/').then((r) => r.data);

// Onboarding seed: create the tenant's default leave types + policies. Idempotent
// (the backend skips if already initialized). Employee balances then auto-populate
// on first read of /leaves/balances. Safe to call best-effort during onboarding.
export const initializeLeaveSetup = () =>
  leaveApi.post('/api/v1/admin/initialize-leave-types').then((r) => r.data);

// --- Holidays (country / company calendar) ---
export type HolidayType = 'public' | 'company' | 'optional';
export interface Holiday {
  id: string;
  name: string;
  date: string; // YYYY-MM-DD
  type: HolidayType;
  is_active: boolean;
  applicable_to?: Record<string, any> | null;
  // Statutory-holiday Phase 2 provenance: 'pack' rows are statutory (managed
  // by the platform holiday pack, read-only here); 'manual'/'seed' are the
  // company's own.
  source?: 'pack' | 'manual' | 'seed';
  country_code?: string | null;
  pack_version?: number | null;
  observed_date?: string | null;
}

export interface HolidayPackSubscription {
  country_code: string;
  locked: boolean;      // mandatory registration-country jurisdiction
  auto: boolean;
  materialized: boolean; // only the locked jurisdiction drives calculations
}

export const getHolidaySubscriptions = () =>
  leaveApi
    .get<{ subscriptions: HolidayPackSubscription[] }>('/api/v1/holidays/subscriptions')
    .then((r) => r.data.subscriptions);

export const addHolidaySubscription = (countryCode: string) =>
  leaveApi.post('/api/v1/holidays/subscriptions', { country_code: countryCode }).then((r) => r.data);

export const removeHolidaySubscription = (countryCode: string) =>
  leaveApi.delete(`/api/v1/holidays/subscriptions/${countryCode}`).then((r) => r.data);

// --- Holiday impact review (statutory-holiday Phase 3) ---
export interface HolidayImpactItem {
  id: string;
  leave_request_id: string;
  employee_id: string;
  impact_type: 'recredited' | 'needs_review' | 'unpaid_corrected' | 'locked_period';
  country_code?: string | null;
  pack_version?: number | null;
  changed_dates?: string[] | null;
  old_days?: number | null;
  new_days?: number | null;
  detail?: Record<string, any> | null;
  status: 'open' | 'resolved';
  resolution_note?: string | null;
  created_at: string;
}

export const getHolidayImpact = (status: 'open' | 'resolved' | 'all' = 'open') =>
  leaveApi
    .get<{ items: HolidayImpactItem[] }>('/api/v1/holidays/impact', { params: { status } })
    .then((r) => r.data.items);

export const resolveHolidayImpact = (id: string, note?: string) =>
  leaveApi.post(`/api/v1/holidays/impact/${id}/resolve`, { note }).then((r) => r.data);

export const getHolidayImpactSettings = () =>
  leaveApi
    .get<{ recredit_mode: 'auto' | 'review' }>('/api/v1/holidays/impact-settings')
    .then((r) => r.data.recredit_mode);

export const setHolidayImpactSettings = (mode: 'auto' | 'review') =>
  leaveApi.put('/api/v1/holidays/impact-settings', { recredit_mode: mode }).then((r) => r.data);
/** Supported country packs for one-click import (matches leave-service seeds). */
export const HOLIDAY_COUNTRIES: { code: string; label: string }[] = [
  { code: 'TZ', label: 'Tanzania' },
  { code: 'KE', label: 'Kenya' },
  { code: 'UG', label: 'Uganda' },
  { code: 'RW', label: 'Rwanda' },
  { code: 'US', label: 'United States' },
];

export const getHolidays = (year?: number, isActive?: boolean) =>
  leaveApi
    .get<Holiday[]>('/api/v1/holidays/', {
      params: { ...(year ? { year } : {}), ...(isActive === undefined ? {} : { is_active: isActive }) },
    })
    .then((r) => r.data);

export const getUpcomingHolidays = (daysAhead = 120) =>
  leaveApi.get<Holiday[]>('/api/v1/holidays/upcoming', { params: { days_ahead: daysAhead } }).then((r) => r.data);

export const createHoliday = (h: { name: string; date: string; type: HolidayType }, allowConflict = false) =>
  leaveApi.post<Holiday>('/api/v1/holidays/', h, allowConflict ? { params: { allow_conflict: true } } : undefined).then((r) => r.data);

export const updateHoliday = (id: string, patch: Partial<Pick<Holiday, 'name' | 'date' | 'type' | 'is_active'>>) =>
  leaveApi.put<Holiday>(`/api/v1/holidays/${id}`, patch).then((r) => r.data);

export const deleteHoliday = (id: string) =>
  leaveApi.delete(`/api/v1/holidays/${id}`).then((r) => r.data);

/** Import a country's public-holiday pack for a given year (idempotent server-side). */
export const importHolidayPack = (countryCode: string, year?: number) =>
  leaveApi
    .post<{ created: number; skipped: number }>(
      `/api/v1/holidays/import-public-holidays/${countryCode}`,
      {},
      { params: year ? { year } : {} },
    )
    .then((r) => r.data);

// --- Tenant country config (drives the default holiday pack) ---
/** Server dry-run of a leave request — the CANONICAL day count (working days,
 * holiday+rest-day aware) + balance/overlap errors. Phase 0 (F6): the UI must
 * never do calendar-day arithmetic for anything decision-bearing. */
/** G5: the employments the signed-in person can take leave from. */
export interface LeaveEmployment {
  engagement_id: string;
  legal_entity_id: string | null;
  company: string;
  is_primary: boolean;
}
export const getMyLeaveEmployments = async (): Promise<{ per_employment: boolean; employments: LeaveEmployment[] }> =>
  (await gatewayApi.get('/api/v1/leaves/my-employments')).data;

export const validateLeaveRequest = async (body: {
  type: string; startDate: string; endDate?: string; dayType?: string; engagementId?: string;
}): Promise<{ valid: boolean; errors: string[]; days_requested: number; leave_type?: string }> =>
  (await gatewayApi.post('/api/v1/leave-requests/validate', body)).data;

export const getTenantCountry = () =>
  authApi.get<{ countryCode: string }>('/api/v1/tenants/country').then((r) => r.data.countryCode);
export const setTenantCountry = (countryCode: string) =>
  authApi.put<{ countryCode: string }>('/api/v1/tenants/country', { countryCode }).then((r) => r.data.countryCode);

// --- Calendar sync (Phase 5: connect Google / Outlook) ---
export type MeetingProvider = 'none' | 'google' | 'microsoft';
export interface CalendarSyncStatus {
  enabled: boolean;
  providers: string[]; // e.g. ['google','microsoft']
  connected: boolean;
  provider: string | null;
  /** Tenant's standard video-meeting provider (drives the scheduling UI). */
  tenant_meeting_provider?: MeetingProvider;
  /** Org credentials configured → links work for everyone, no personal connect. */
  org_meeting_links?: boolean;
  /** Provider-side account this connection writes to (when known) — that
   * account is the Meet/Teams host of links minted through it. */
  account_email?: string | null;
}
export const getCalendarSyncStatus = () =>
  leaveApi.get<CalendarSyncStatus>('/api/v1/calendar-sync/status').then((r) => r.data);
export const getCalendarConnectUrl = (provider: string) =>
  leaveApi.get<{ url: string }>(`/api/v1/calendar-sync/connect/${provider}`).then((r) => r.data.url);
export const disconnectCalendar = () =>
  leaveApi.post('/api/v1/calendar-sync/disconnect').then((r) => r.data);

/** Per-user reminder settings for synced external meetings. */
export interface CalendarReminderPrefs {
  enabled: boolean;
  offsets: number[];        // minutes-before, e.g. [30, 5]
  channels: string[];       // 'push' | 'in_app' | 'email'
}
export const getCalendarReminderPrefs = () =>
  leaveApi.get<CalendarReminderPrefs>('/api/v1/calendar-sync/reminder-preferences').then((r) => r.data);
export const setCalendarReminderPrefs = (prefs: Partial<CalendarReminderPrefs>) =>
  leaveApi.put<CalendarReminderPrefs>('/api/v1/calendar-sync/reminder-preferences', prefs).then((r) => r.data);

// Admin (tenant-level enable toggle, Settings → Integrations)
export interface CalendarAdminSettings {
  platform_available: boolean; // platform has OAuth creds configured
  providers: string[];
  enabled: boolean; // this tenant's toggle
  meeting_provider?: MeetingProvider; // tenant's standard video-meeting provider
}
// Org-level meeting credentials (Phase 2): one org account, links for everyone.
export interface OrgCredentialStatus {
  configured: boolean;
  status?: 'configured' | 'verified' | 'error' | null;
  last_error?: string | null;
  connected_at?: string | null;
  admin_consent_url?: string; // microsoft only
  platform_app_configured?: boolean; // microsoft only
}
export const getOrgMeetingStatus = (provider: 'google' | 'microsoft') =>
  leaveApi.get<OrgCredentialStatus>(`/api/v1/calendar-sync/admin/org/${provider}`).then((r) => r.data);
export const setGoogleOrgCredential = (serviceAccountJson: string) =>
  leaveApi
    .post<OrgCredentialStatus>('/api/v1/calendar-sync/admin/org/google', { service_account_json: serviceAccountJson })
    .then((r) => r.data);
export const setMicrosoftOrgCredential = (directoryId: string) =>
  leaveApi
    .post<OrgCredentialStatus>('/api/v1/calendar-sync/admin/org/microsoft', { directory_id: directoryId })
    .then((r) => r.data);
export const verifyOrgCredential = (provider: 'google' | 'microsoft', email?: string) =>
  leaveApi
    .post<{ ok: boolean; error?: string }>(`/api/v1/calendar-sync/admin/org/${provider}/verify`, { email })
    .then((r) => r.data);
export const deleteOrgCredential = (provider: 'google' | 'microsoft') =>
  leaveApi.delete(`/api/v1/calendar-sync/admin/org/${provider}`).then((r) => r.data);

export const getCalendarAdminSettings = () =>
  leaveApi.get<CalendarAdminSettings>('/api/v1/calendar-sync/admin/settings').then((r) => r.data);
export const setCalendarAdminSettings = (enabled: boolean, meetingProvider?: MeetingProvider) =>
  leaveApi
    .put<CalendarAdminSettings>('/api/v1/calendar-sync/admin/settings', {
      enabled,
      ...(meetingProvider !== undefined ? { meeting_provider: meetingProvider } : {}),
    })
    .then((r) => r.data);

/** Best-effort: seed a new company's holidays on signup. Statutory-holiday
 *  Phase 2: listing subscriptions lazily ensures the registration-country
 *  jurisdiction server-side and materializes the published statutory pack —
 *  the legacy hardcoded import is only a fallback if no pack exists yet.
 *  Idempotent server-side, so safe to call on signup. Never throws. */
export const seedTenantHolidays = async (countryCode = 'TZ') => {
  try {
    const subs = await getHolidaySubscriptions();
    if (subs.some((s) => s.locked)) return; // pack pipeline took over
  } catch { /* fall through to the legacy import */ }
  const y = new Date().getFullYear();
  try { await importHolidayPack(countryCode, y); } catch { /* best-effort */ }
  try { await importHolidayPack(countryCode, y + 1); } catch { /* best-effort */ }
};

// ============================================================================
// Benefits Service (/api/v1/benefits/*) — settings, providers, plans,
// enrollments, allowances, deductions, retirement. Per-employee lists for the
// payroll-impacting records; tenant-wide lists for plans/providers.
// ============================================================================

export interface BenefitsSettings {
  benefits_enabled: boolean;
  insurance_enabled: boolean;
  retirement_enabled: boolean;
  allowances_enabled: boolean;
  deductions_enabled: boolean;
  self_enrollment_enabled: boolean;
  hr_approval_required: boolean;
  show_employee_view: boolean;
}
export const getBenefitsSettings = () =>
  benefitsApi.get<BenefitsSettings>('/api/v1/benefits/settings').then((r) => r.data);
export const updateBenefitsSettings = (patch: Partial<BenefitsSettings>) =>
  benefitsApi.put<BenefitsSettings>('/api/v1/benefits/settings', patch).then((r) => r.data);

export interface BenefitProvider {
  id: string; name: string; type: string;
  contact_name?: string; contact_email?: string; contact_phone?: string;
  website?: string; notes?: string; status: string;
}
export const listProviders = () =>
  benefitsApi.get<{ providers: BenefitProvider[] }>('/api/v1/benefits/providers').then((r) => r.data.providers);
export const createProvider = (input: Partial<BenefitProvider>) =>
  benefitsApi.post<BenefitProvider>('/api/v1/benefits/providers', input).then((r) => r.data);
export const updateProvider = (id: string, input: Partial<BenefitProvider>) =>
  benefitsApi.patch<BenefitProvider>(`/api/v1/benefits/providers/${id}`, input).then((r) => r.data);

export interface BenefitPlan {
  id: string; name: string; description?: string; type: string;
  provider_id?: string | null;
  employee_contribution: number; employer_contribution: number;
  contribution_type: string; taxable: boolean; payroll_impact: string;
  effective_start_date?: string | null; effective_end_date?: string | null;
  status: string;
}
export const listPlans = () =>
  benefitsApi.get<{ plans: BenefitPlan[] }>('/api/v1/benefits/plans').then((r) => r.data.plans);
export const createPlan = (input: Partial<BenefitPlan>) =>
  benefitsApi.post<BenefitPlan>('/api/v1/benefits/plans', input).then((r) => r.data);
export const updatePlan = (id: string, input: Partial<BenefitPlan>) =>
  benefitsApi.patch<BenefitPlan>(`/api/v1/benefits/plans/${id}`, input).then((r) => r.data);
export const activatePlan = (id: string) =>
  benefitsApi.post<BenefitPlan>(`/api/v1/benefits/plans/${id}/activate`).then((r) => r.data);
export const archivePlan = (id: string) =>
  benefitsApi.post<BenefitPlan>(`/api/v1/benefits/plans/${id}/archive`).then((r) => r.data);
export const deletePlan = (id: string) =>
  benefitsApi.delete(`/api/v1/benefits/plans/${id}`).then((r) => r.data);

// --- Music benefit (provider-neutral digital-subscription) -------------------
// Employer-sponsored music benefit. The employer pays; the employee activates
// against their own personal provider account. No listening data is ever exposed.
export interface MusicEntitlement {
  id: string; plan_id: string; status: string;
  eligibility_reason?: string; activation_method?: string;
  provider_status?: string; funding_start_date?: string | null;
}
export interface MusicProviderLink {
  id: string; provider_key: string; status: string; provider_ref?: string;
  scopes?: string[]; token_expires_at?: string | null;
}
const MB = '/api/v1/benefits/subscriptions';
export const getMyMusicBenefit = () =>
  benefitsApi.get<{ entitlements: MusicEntitlement[] }>(`${MB}/me`).then((r) => r.data.entitlements);
export const activateMusicBenefit = (id: string) =>
  benefitsApi.post<MusicEntitlement>(`${MB}/me/entitlements/${id}/activate`).then((r) => r.data);
export const declineMusicBenefit = (id: string) =>
  benefitsApi.post<MusicEntitlement>(`${MB}/me/entitlements/${id}/decline`).then((r) => r.data);
export const getMusicLink = () =>
  benefitsApi.get<MusicProviderLink>(`${MB}/me/link`).then((r) => r.data).catch(() => null);
export const beginMusicLink = (body: { country?: string; currency?: string; return_url?: string; state?: string }) =>
  benefitsApi.post<{ url: string }>(`${MB}/me/link/begin`, body).then((r) => r.data);
export const completeMusicLink = (body: { code: string; state: string }) =>
  benefitsApi.post<MusicProviderLink>(`${MB}/me/link/complete`, body).then((r) => r.data);
export const disconnectMusicLink = () =>
  benefitsApi.delete(`${MB}/me/link`).then((r) => r.data);
export const openMusicPlayback = (body: { context_ref?: string } = {}) =>
  benefitsApi.post<{ url: string; kind: string }>(`${MB}/me/playback-link`, body).then((r) => r.data);

export interface BenefitEnrollment {
  id: string; employee_id: string; benefit_plan_id: string;
  coverage_level: string; status: string;
  effective_start_date?: string | null; effective_end_date?: string | null;
}
export const listEnrollments = (employeeId: string) =>
  benefitsApi.get<{ enrollments: BenefitEnrollment[] }>(`/api/v1/benefits/employees/${employeeId}/enrollments`).then((r) => r.data.enrollments);
export const hrEnroll = (input: { employee_id: string; benefit_plan_id: string; coverage_level?: string; effective_start_date?: string }) =>
  benefitsApi.post<BenefitEnrollment>('/api/v1/benefits/enrollments', input).then((r) => r.data);
export const approveEnrollment = (id: string) =>
  benefitsApi.post<BenefitEnrollment>(`/api/v1/benefits/enrollments/${id}/approve`).then((r) => r.data);
export const declineEnrollment = (id: string) =>
  benefitsApi.post<BenefitEnrollment>(`/api/v1/benefits/enrollments/${id}/decline`).then((r) => r.data);
export const terminateEnrollment = (id: string) =>
  benefitsApi.post<BenefitEnrollment>(`/api/v1/benefits/enrollments/${id}/terminate`).then((r) => r.data);

export interface EmployeeAllowance {
  id: string; employee_id: string; allowance_type: string; name: string;
  amount: number; currency: string; taxable: boolean; in_kind?: boolean; recurring: boolean;
  frequency: string; status: string;
  effective_start_date?: string | null; effective_end_date?: string | null;
}

// Company-wide allowances/benefits — apply to EVERY active employee; the payroll
// lookup unions them into each employee's pay (whole-company + individual impact).
// in_kind = non-cash taxable benefit (taxed via PAYE, not paid as cash).
export interface CompanyAllowance {
  id: string; allowance_type: string; name: string; amount: number; currency: string;
  taxable: boolean; in_kind: boolean; frequency: string; status: string;
  effective_start_date?: string | null; effective_end_date?: string | null;
}
export const listCompanyAllowances = () =>
  benefitsApi.get<{ company_allowances: CompanyAllowance[] }>('/api/v1/benefits/company-allowances').then((r) => r.data.company_allowances ?? []);
export const createCompanyAllowance = (input: Partial<CompanyAllowance>) =>
  benefitsApi.post<CompanyAllowance>('/api/v1/benefits/company-allowances', input).then((r) => r.data);
export const deleteCompanyAllowance = (id: string) =>
  benefitsApi.delete(`/api/v1/benefits/company-allowances/${id}`).then((r) => r.data);
export const listAllowances = (employeeId: string) =>
  benefitsApi.get<{ allowances: EmployeeAllowance[] }>(`/api/v1/benefits/employees/${employeeId}/allowances`).then((r) => r.data.allowances);
export const createAllowance = (employeeId: string, input: Partial<EmployeeAllowance>) =>
  benefitsApi.post<EmployeeAllowance>(`/api/v1/benefits/employees/${employeeId}/allowances`, input).then((r) => r.data);
export const approveAllowance = (id: string) =>
  benefitsApi.post<EmployeeAllowance>(`/api/v1/benefits/allowances/${id}/approve`).then((r) => r.data);
export const endAllowance = (id: string) =>
  benefitsApi.post<EmployeeAllowance>(`/api/v1/benefits/allowances/${id}/end`).then((r) => r.data);

export interface EmployeeDeduction {
  id: string; employee_id: string; deduction_type: string; name: string;
  amount: number; currency: string; pre_tax: boolean; recurring: boolean;
  frequency: string; status: string;
  effective_start_date?: string | null; effective_end_date?: string | null;
}
export const listDeductions = (employeeId: string) =>
  benefitsApi.get<{ deductions: EmployeeDeduction[] }>(`/api/v1/benefits/employees/${employeeId}/deductions`).then((r) => r.data.deductions);
export const createDeduction = (employeeId: string, input: Partial<EmployeeDeduction>) =>
  benefitsApi.post<EmployeeDeduction>(`/api/v1/benefits/employees/${employeeId}/deductions`, input).then((r) => r.data);
export const approveDeduction = (id: string) =>
  benefitsApi.post<EmployeeDeduction>(`/api/v1/benefits/deductions/${id}/approve`).then((r) => r.data);
export const endDeduction = (id: string) =>
  benefitsApi.post<EmployeeDeduction>(`/api/v1/benefits/deductions/${id}/end`).then((r) => r.data);

export interface RetirementContribution {
  id: string; employee_id: string; retirement_provider: string;
  membership_number?: string;
  employee_contribution_rate: number; employer_contribution_rate: number;
  contribution_type: string; status: string;
  effective_start_date?: string | null; effective_end_date?: string | null;
}
export const listRetirement = (employeeId: string) =>
  benefitsApi.get<{ retirement_contributions: RetirementContribution[] }>(`/api/v1/benefits/employees/${employeeId}/retirement`).then((r) => r.data.retirement_contributions);
export const createRetirement = (employeeId: string, input: Partial<RetirementContribution>) =>
  benefitsApi.post<RetirementContribution>(`/api/v1/benefits/employees/${employeeId}/retirement`, input).then((r) => r.data);
export const endRetirement = (id: string) =>
  benefitsApi.post<RetirementContribution>(`/api/v1/benefits/retirement/${id}/end`).then((r) => r.data);

// Tenant-wide audit lists (privileged roles) — every allowance/deduction across
// the company, for HR/payroll review. Optional status filter.
export const listAllAllowances = (status?: string) =>
  benefitsApi
    .get<{ allowances: EmployeeAllowance[] }>(`/api/v1/benefits/allowances${status ? `?status=${status}` : ''}`)
    .then((r) => r.data.allowances);
export const listAllDeductions = (status?: string) =>
  benefitsApi
    .get<{ deductions: EmployeeDeduction[] }>(`/api/v1/benefits/deductions${status ? `?status=${status}` : ''}`)
    .then((r) => r.data.deductions);

// My Benefits self-view (/api/v1/benefits/me/*) — the caller's OWN records;
// employee id is resolved server-side, so no id is passed from the client.
export const getMyAllowances = () =>
  benefitsApi.get<{ allowances: EmployeeAllowance[] }>('/api/v1/benefits/me/allowances').then((r) => r.data.allowances);
export const getMyDeductions = () =>
  benefitsApi.get<{ deductions: EmployeeDeduction[] }>('/api/v1/benefits/me/deductions').then((r) => r.data.deductions);
export const getMyRetirement = () =>
  benefitsApi.get<{ retirement_contributions: RetirementContribution[] }>('/api/v1/benefits/me/retirement').then((r) => r.data.retirement_contributions);
export const getMyEnrollments = () =>
  benefitsApi.get<{ enrollments: BenefitEnrollment[] }>('/api/v1/benefits/me/enrollments').then((r) => r.data.enrollments);
export const selfEnroll = (input: { benefit_plan_id: string; coverage_level?: string; effective_start_date?: string }) =>
  benefitsApi.post<BenefitEnrollment>('/api/v1/benefits/me/enrollments', input).then((r) => r.data);

/** Bulk display-name resolution for people referenced by id (audit actors,
 *  delegations, activity). Uses employee-service's directory-safe /names
 *  endpoint; unresolvable ids are simply absent. Never throws. */
export interface PersonNameHit { id: string; user_id: string | null; name: string; email?: string | null; avatar_url?: string | null }
export const resolvePeopleNames = async (ids: { userIds?: string[]; employeeIds?: string[] }): Promise<Record<string, string>> => {
  const user_ids = Array.from(new Set((ids.userIds ?? []).filter(Boolean)));
  const employee_ids = Array.from(new Set((ids.employeeIds ?? []).filter(Boolean)));
  if (!user_ids.length && !employee_ids.length) return {};
  try {
    const { data } = await employeeApi.post<{ items: PersonNameHit[] }>('/api/v1/employees/names', { user_ids, employee_ids });
    const out: Record<string, string> = {};
    for (const it of data?.items ?? []) {
      if (it.name) {
        if (it.id) out[it.id] = it.name;
        if (it.user_id) out[it.user_id] = it.name;
      }
    }
    return out;
  } catch {
    return {};
  }
};

// --- duplicate people ----------------------------------------------------------
//
// Every duplicate found in production was a typo that produced a SECOND record
// for somebody already here. The check runs before a person is added; the list
// finds the ones already sitting in the directory.

export type DuplicateReason = 'same_email' | 'similar_email' | 'same_name';

export interface DuplicateRecord {
  employeeId: string;
  name: string;
  email: string;
  status: string;
  hasLogin: boolean;
}

export interface DuplicateMatch extends DuplicateRecord {
  reason: DuplicateReason;
}

/** Does this person look like somebody already here? A WARNING — never a block:
 *  two people genuinely can share a name, and refusing the second would stop
 *  somebody being paid. Fails soft: a check that errors must not prevent an
 *  invitation. */
export interface DuplicateCheck {
  matches: DuplicateMatch[];
  /** The address belongs to a FUNCTION, not a person (hr@, tech@, info@).
   *  A different statement from "already here" and shown differently. */
  sharedAddress: boolean;
}

export const checkDuplicatePerson = (q: {
  first_name?: string; last_name?: string; email?: string; exclude_employee_id?: string;
}): Promise<DuplicateCheck> =>
  employeeApi
    .get<DuplicateCheck>('/api/v1/employees/duplicates/check', { params: q })
    .then((r) => ({ matches: r.data.matches ?? [], sharedAddress: !!r.data.sharedAddress }))
    .catch(() => ({ matches: [], sharedAddress: false }));

export interface DuplicateGroup { reason: DuplicateReason; records: DuplicateRecord[] }

export const listPossibleDuplicates = (): Promise<DuplicateGroup[]> =>
  employeeApi
    .get<{ groups: DuplicateGroup[] }>('/api/v1/employees/duplicates')
    .then((r) => r.data.groups ?? [])
    .catch(() => []);
